import minBy from 'lodash/minBy'
import sortBy from 'lodash/sortBy'
import uniqBy from 'lodash/uniqBy'
import type {ParserT} from '@freckle/parser'
import {
  Parser,
  boolean,
  number,
  string,
  record,
  merge,
  nullableDefault,
  nullable,
  array
} from '@freckle/parser'
import type {LangT} from '@freckle/educator-entities/ts/common/helpers/languages'
import type {RlDomainT} from '@freckle/educator-entities/ts/common/models/rl-domain'
import {parseDomainAttrs} from '@freckle/educator-entities/ts/common/models/rl-domain'
import type {RlDomainIdT} from '@freckle/educator-entities/ts/common/types/rl-domain-id'
import type {RlSkillUspIdT} from '@freckle/educator-entities/ts/common/types/rl-skill-usp-id'
import type {RlStandardIdT} from '@freckle/educator-entities/ts/common/types/rl-standard-id'
import {curriculaClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'

type RlStandardShared = {
  id: RlStandardIdT
  name: string // Standard "Code," e.g. "K.CC.1"
  shortName: string // "Name" for the standard, e.g. "Count to 100"
  description: string // Longer form, e.g. "Count to 100 by ones and by tens."
  progressionOrder: number
  grade: number
  domainId: RlDomainIdT
  hasElaGrammarQuestions: boolean
  hasElaPathwayContent: boolean
  hasElaWordStudyQuestions: boolean
  hasElaArticleWritingQuestions: boolean
  hasElaArticleReadingQuestions: boolean
  hasMathDok?: boolean | null // TODO: null here is awkward https://app.asana.com/0/1205102828747541/1205952740674317
  isStudentVisible: boolean
}

export type DehydratedStandard = {
  id: RlStandardIdT
}

export const parseDehydratedStandard: ParserT<DehydratedStandard> = record({
  id: string()
})

export type RlStandardT = {
  // TODO: remove this nullable type and use RlStandardWithDomainT when appropriate
  // https://app.asana.com/0/149473556304568/1200915504043803/f
  domain?: RlDomainT | null
} & RlStandardShared

export type RlStandardWithDomainT = {
  domain: RlDomainT
} & RlStandardShared

// TODO: This function is here for transitionary purposes only. It can be removed once the `domain` is removed from RlStandardT
// https://app.asana.com/0/149473556304568/1200915504043803/f
export const toRlStandard = (rlStandardWithDomain: RlStandardWithDomainT): RlStandardT => ({
  ...rlStandardWithDomain
})

// TODO: This function is here for transitionary purposes only. It can be removed once the `domain` is removed from RlStandardT
// https://app.asana.com/0/149473556304568/1200915504043803/f
export const toRlDomain = (rlStandardWithDomain: RlStandardWithDomainT): RlDomainT => {
  return rlStandardWithDomain.domain
}

export const parseStandardAttrs: ParserT<RlStandardT> = record({
  id: string(),
  name: string(),
  shortName: string(),
  description: string(),
  progressionOrder: number(),
  grade: number(),
  domainId: string(),
  domain: nullable(parseDomainAttrs),
  hasElaGrammarQuestions: nullableDefault(boolean(), false),
  hasElaPathwayContent: nullableDefault(boolean(), false),
  hasElaWordStudyQuestions: nullableDefault(boolean(), false),
  hasElaArticleWritingQuestions: nullableDefault(boolean(), false),
  hasElaArticleReadingQuestions: nullableDefault(boolean(), false),
  hasMathDok: nullable(boolean()),
  isStudentVisible: nullableDefault(boolean(), true)
})

export const parseRlStandards = Parser.mkRun<RlStandardT[]>(array(parseStandardAttrs))

const parseDomainAttrsObj: ParserT<{
  domain: RlDomainT
}> = record({domain: parseDomainAttrs})

export const parseStandardWithDomainAttrs: ParserT<RlStandardWithDomainT> = merge(
  parseStandardAttrs,
  parseDomainAttrsObj
)

export const parseRlStandardWithDomain = Parser.mkRun<RlStandardWithDomainT>(
  parseStandardWithDomainAttrs
)
export const parseRlStandardWithDomains = Parser.mkRun<RlStandardWithDomainT[]>(
  array(parseStandardWithDomainAttrs)
)

/**
 * API
 */

type FetchRlStandardsParamsT = {
  domainId?: RlDomainIdT
}

export function fetchRlStandardsWithDomainWhere(
  contentArea: 'math' | 'ela' | 'social-studies' | 'science',
  standardSetId: string,
  lang: LangT,
  predicate: (a: RlStandardWithDomainT) => boolean
): Promise<Array<RlStandardWithDomainT>> {
  return curriculaClient
    .GET('/{content-area}/standard-sets/{id}/{lang}/standards.json', {
      params: {
        path: {
          'content-area': contentArea,
          id: standardSetId,
          lang
        }
      }
    })
    .then(extractData)
    .then(response => {
      const standards = parseRlStandardWithDomains(response).filter(predicate)
      return sortBy(standards, ['lowestGrade', 'highestGrade', 'domainId', 'progressionOrder'])
    })
}

export function fetchRlStandardsWithDomain(
  contentArea: 'math' | 'ela' | 'social-studies' | 'science',
  standardSetId: string,
  lang: LangT,
  params?: FetchRlStandardsParamsT
): Promise<Array<RlStandardWithDomainT>> {
  const domainId = params && params.domainId !== undefined ? params.domainId : null
  return fetchRlStandardsWithDomainWhere(contentArea, standardSetId, lang, s =>
    domainId === null ? true : s.domainId === domainId
  )
}

export const fetchContentContainingElaRlStandardsWithDomain = (
  elaStandardSetId: string,
  lang: LangT
): Promise<Array<RlStandardWithDomainT>> =>
  fetchRlStandardsWithDomainWhere(
    'ela',
    elaStandardSetId,
    lang,
    s => s.hasElaPathwayContent || s.hasElaGrammarQuestions
  )

/**
 * Grouping rl-standards by domain and name is the temporary workaround we are using
 * to address the following scenario:
 *
 * As part of our data migration from legacy fr-standards to rl-standards,
 * if N FR standards were mapped to a single state standard, preserving
 * behavior-neutral student progression required the creation of N
 * corresponding state standards (even though there really is only 1 state
 * standard).  This resulted in N state standards that look like duplicates
 * (same name and description). To address this for now, we will remove
 * duplicates within a domain and return the first standard in progression order.
 *
 * Note that if a duplicate spans across domains, we are keeping it in.
 *
 */

export function removeRlStandardNameDuplicatesWithinDomains(
  rlStandards: Array<RlStandardWithDomainT>
): Array<RlStandardWithDomainT> {
  const empty: Record<string, RlStandardWithDomainT[]> = {}
  const pairs = rlStandards.reduce((results, s) => {
    const key = `${s.domainId}${s.name}`
    const existing = results[key]
    if (existing !== undefined) {
      existing.push(s)
    } else {
      results[key] = [s]
    }
    return results
  }, empty)
  const standardsToDisplay = Object.values(pairs).flatMap(standards => {
    const firstStandard = minBy(standards, s => s.progressionOrder)
    if (standards.length > 1 && firstStandard !== undefined) {
      return [firstStandard]
    } else {
      return standards
    }
  })

  return standardsToDisplay
}

export function safeExtractDomains(rlStandards: Array<RlStandardWithDomainT>): Array<RlDomainT> {
  const domains = rlStandards.map(standard => standard.domain)
  return sortBy(
    uniqBy(domains, domain => domain.id),
    ['lowestGrade', 'highestGrade']
  )
}

/**
 * Fetch the domains of an RL standard set
 *
 * note: If added to rl-domain.js this causes a cyclic dependency. Its presence here is temporary.
 **/
export async function fetchRlDomains(
  contentArea: 'math' | 'ela' | 'social-studies' | 'science',
  standardSet: Inexact<{id: string}>,
  lang: LangT
): Promise<Array<RlDomainT>> {
  const rlStandards = await fetchRlStandardsWithDomain(contentArea, standardSet.id, lang)
  return safeExtractDomains(rlStandards)
}

export function filterRlStandardsByGradeAndDomain(
  rlStandards: Array<RlStandardWithDomainT>,
  grade: number,
  rlDomainId: RlDomainIdT
): Array<RlStandardWithDomainT> {
  return rlStandards.filter(s => s.grade === grade && s.domainId === rlDomainId)
}

export type RlStandardMapT = Map<RlStandardIdT, RlStandardWithDomainT>
export function makeRlStandardMap(rlStandards: Array<RlStandardWithDomainT>): RlStandardMapT {
  const rlStandardsMap = new Map()

  rlStandards.forEach(rlStandard => {
    rlStandardsMap.set(rlStandard.id, rlStandard)
  })

  return rlStandardsMap
}

// Lookup table from Freckle RL Skill UspId to RL standard
export type RlStandardByRLSkillUspIdMapT = Map<RlSkillUspIdT, RlStandardWithDomainT>

export const mkStandardBySkillIdMap = (
  rlStandards: Array<RlStandardWithDomainT>,
  rlSkills: Array<{standard: string; uspId: string}>
): RlStandardByRLSkillUspIdMapT =>
  new Map(
    rlSkills.flatMap(rlSkill =>
      rlStandards
        .filter(rlStandard => rlStandard.id === rlSkill.standard)
        .map(rlStandard => [rlSkill.uspId, rlStandard])
    )
  )
